import * as React from 'react';

import { useClient } from '@playbooks/contexts';
import { Button } from '@playbooks/ui/components/button';
import { FarIcon } from '@playbooks/ui/components/icon';
import { Link } from '@playbooks/ui/components/link';
import { cn } from '@playbooks/ui/lib/utils';

type AccountNavigationItem = {
	label: string;
	icon: string;
	href?: string;
	view?: string;
	external?: boolean;
	destructive?: boolean;
};

const sharedItems: AccountNavigationItem[] = [
	{ label: 'Appearance', icon: 'circle-half-stroke', view: 'appearance' },
	{ label: 'Developers', icon: 'code-simple', view: 'developers' },
	{ label: 'Resources', icon: 'shapes', view: 'resources' },
	{ label: 'Support', icon: 'headset', view: 'support' },
	{ label: 'Community', icon: 'messages', view: 'community' },
];

type AccountNavigationListProps = {
	signedIn?: boolean;
	profileHref?: string;
	target?: string;
	onView: (view: string) => void;
	onLogout?: () => void;
	className?: string;
};

function AccountNavigationList({
	signedIn,
	profileHref = '/',
	onView,
	onLogout,
	className = '',
}: AccountNavigationListProps) {
	const { platformHref } = useClient();

	// Computed
	const accountItems: AccountNavigationItem[] = signedIn
		? [
				{ label: 'Profile', icon: 'circle-user', href: profileHref },
				{ label: 'Settings', icon: 'cog', href: '/settings' },
			]
		: [{ label: 'Sign in', icon: 'right-to-bracket', href: '/auth' }];
	const items = [...accountItems, ...sharedItems, { label: 'Homepage', icon: 'home', href: '/' }];

	// Theme
	const rootClassName = cn('space-y-0.5 p-1', className);

	// Render
	return (
		<div data-slot='account-navigation-list' className={rootClassName}>
			{items.map(item => {
				const content = (
					<>
						<FarIcon icon={item.icon} />
						<span className='min-w-0 flex-1 truncate text-left'>{item.label}</span>
						{item.view ? <FarIcon icon='chevron-right' className='size-3 text-foreground-body' /> : null}
						{item.external ? <FarIcon icon='arrow-up-right' className='size-3 text-foreground-body' /> : null}
					</>
				);
				if (item.href) {
					return (
						<Link
							key={item.label}
							href={item.external ? item.href : platformHref(item.href)}
							target={item.external ? '_blank' : undefined}
							rel={item.external ? 'noopener noreferrer' : undefined}
							variant='ghost'
							className='w-full justify-start'>
							{content}
						</Link>
					);
				}
				return (
					<Button
						key={item.label}
						type='button'
						variant='ghost'
						className='w-full justify-start'
						onClick={() => onView(item.view)}>
						{content}
					</Button>
				);
			})}
			{signedIn && onLogout ? (
				<div className='-mx-1 mt-1 border-t border-border px-1 pt-1'>
					<Button type='button' variant='ghost' className='w-full justify-start' onClick={onLogout}>
						<FarIcon icon='power-off' />
						Logout
					</Button>
				</div>
			) : null}
		</div>
	);
}

export { AccountNavigationList };
